import { motion } from "framer-motion";
import { Check, TrendingDown } from "lucide-react";
import { waLink, PROCESS_STEPS, BUSINESS_SIZES, BUSINESS_BENEFITS } from "../../data/site";
import { Reveal } from "./Reveal";
import { WhatsAppIcon } from "./WhatsAppIcon";

export const Business = () => (
  <section id="empresas" data-testid="business-section" className="relative overflow-hidden bg-graphite py-24 sm:py-32">
    <div className="pointer-events-none absolute inset-0 grid-dark opacity-60" />
    <div className="pointer-events-none absolute -left-40 top-20 h-[420px] w-[420px] rounded-full bg-champagne/10 blur-3xl" />

    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="flex items-center gap-3 font-mono text-sm font-semibold uppercase tracking-[0.3em] text-champagne sm:text-base">
          <span className="h-px w-10 bg-champagne/60" />
          Empresas
        </p>
        <h2 className="mt-4 max-w-3xl font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
          Cuide da saúde da sua equipe com uma solução adequada para sua empresa.
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-stone-400 sm:text-lg">
          Trabalho com empresas de pequeno e grande porte, analisando o perfil e
          as necessidades de cada empresa para apresentar opções de planos
          empresariais adequadas ao seu momento.
        </p>
      </Reveal>

      <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-3">
        {BUSINESS_SIZES.map((s, i) => (
          <Reveal key={s.k} delay={i * 0.1}>
            <div
              data-testid={`business-size-${i}`}
              className="group h-full rounded-3xl border border-white/10 bg-white/[0.04] p-8 transition-[border-color,background-color,transform] duration-500 hover:-translate-y-1.5 hover:border-champagne/40 hover:bg-white/[0.07]"
            >
              <p className="font-display text-2xl font-semibold tracking-tight text-white transition-colors duration-500 group-hover:text-champagne sm:text-3xl">
                {s.k}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-stone-400">{s.text}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16">
        <div
          data-testid="business-savings-highlight"
          className="relative overflow-hidden rounded-[2rem] border border-champagne/30 bg-champagne/[0.08] px-6 py-12 text-center sm:px-12 sm:py-14"
        >
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-champagne/15 text-champagne">
            <TrendingDown className="h-6 w-6" strokeWidth={1.8} />
          </span>
          <p className="mx-auto mt-6 max-w-3xl font-display text-2xl font-semibold leading-snug tracking-tight text-white sm:text-3xl">
            Você sabia que um plano empresarial pode gerar uma economia de{" "}
            <em className="italic text-champagne">até 45%</em> em comparação ao
            plano coletivo por adesão?
          </p>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-stone-400 sm:text-base">
            Com seu CNPJ, você pode reduzir o custo do plano e também melhorar a
            qualidade da sua assistência.
          </p>
        </div>
      </Reveal>

      <div className="mt-16">
        <Reveal>
          <h3 className="font-display text-xl font-semibold text-white sm:text-2xl">
            Entenda os benefícios
          </h3>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {BUSINESS_BENEFITS.map((b, i) => (
            <Reveal key={b.text} delay={i * 0.06}>
              <div
                data-testid={`business-benefit-${i}`}
                className={`flex h-full items-start gap-4 rounded-2xl border p-6 transition-[border-color,background-color,transform] duration-500 hover:-translate-y-1 ${
                  b.highlight
                    ? "border-champagne/60 bg-champagne/10 hover:border-champagne"
                    : "border-white/10 bg-white/[0.04] hover:border-champagne/30"
                }`}
              >
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-champagne" strokeWidth={2.5} />
                <div>
                  {b.highlight && (
                    <span className="mb-2 inline-block rounded-full bg-champagne/20 px-3 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-champagne">
                      Principal vantagem
                    </span>
                  )}
                  <p className={`font-display font-semibold tracking-tight text-white ${b.highlight ? "text-xl sm:text-2xl" : "text-lg"}`}>
                    {b.text}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal className="mt-16 text-center">
        <p className="font-display text-2xl font-semibold tracking-tight text-white sm:text-3xl">
          Receba uma cotação personalizada.
        </p>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-stone-400 sm:text-base">
          Entre em contato comigo e encontre uma opção adequada para sua empresa.
        </p>
        <a
          href={waLink("Olá! Quero uma cotação personalizada de plano empresarial para minha empresa.")}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="business-cta"
          className="group mt-8 inline-flex items-center gap-3 rounded-full bg-wa px-8 py-4 text-sm font-bold text-white shadow-[0_12px_40px_-10px_rgba(37,211,102,0.45)] transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-wa-dark"
        >
          <WhatsAppIcon className="h-5 w-5 transition-transform duration-300 group-hover:rotate-6" />
          Quero uma análise para minha empresa
        </a>
      </Reveal>

      <div className="mt-20">
        <Reveal>
          <h3 className="font-display text-xl font-semibold text-white sm:text-2xl">
            Uma análise personalizada para encontrar o plano ideal
          </h3>
        </Reveal>
        <div className="relative mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 top-7 hidden h-px w-full origin-left bg-gradient-to-r from-champagne via-champagne/50 to-transparent lg:block"
          />
          {PROCESS_STEPS.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.15}>
              <div data-testid={`process-step-${step.n}`} className="relative">
                <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full border border-champagne/40 bg-graphite font-display text-lg font-semibold text-champagne">
                  {step.n}
                </span>
                <h4 className="mt-5 font-display text-base font-semibold text-white">
                  {step.title}
                </h4>
                <p className="mt-2 text-sm leading-relaxed text-stone-500">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
